/* ++++++++++ --------------- IMPORTS --------------- ++++++++++ */
// libraries
import React, { useEffect, useCallback, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchCommentingConfigV2 } from 'state/thunks/commenting';
import { fetchTrendingNowV2Config } from 'state/thunks/trendingnowv2';
import { updateCurrentPage } from 'state/slices/app';
// utils
// material
import { Page, Content, InContentMessage, InlineMessage, EmptyState, log } from '@insticator/insticator-ui';
import PageHeader from "material/structure/pageheader/pageheader";
import PageContent from "material/structure/pagecontent/pagecontent";
import SiteFilter from 'material/sitefilter/sitefilter';
import LoadingState from 'material/states/loading/loading';
import CardWrapper from 'material/cardwrapper/cardwrapper';
// content
import GeneralSection from './generalsection/generalsection';
import AdvancedSection from './advancedsection/advancedsection';
import AdminSection from './adminsection/adminsection';
// styles
import injectSheet from "react-jss";
import styles from './commentingconfigv2_styles';
import SiteThemeSection from './sitethemesection/sitethemesection';
import AdsSection from './adssection/adssection';
import { useIsAdminUser } from 'utils/session';


/* ========== ~~~~~~~~~~ COMMENTING CONFIG V2 PAGE ~~~~~~~~~~ ========== */
const CommentingConfigV2Page = injectSheet(styles)((props) => {
  const dispatch = useDispatch();
  const { siteUUID, accountUUID } = useSelector(state => state.session);
  const { commentingUnitConfig, displayConfig } = useSelector(state => state.commentingV2)
  const { migrationStatus, sitesWithProductsV2 } = useSelector(state => state.migrationStatus)

  const { accountProducts } = useSelector(state => state.account);
  const hasTrendingNowV2 = accountProducts.includes('TRENDING_NOW_V2')
  const { trendingNowId } = useSelector(state => state.trendingNowV2.trendingNowConfig)
  const [isLoading,setIsLoading] = useState(true)
  const isAdmin = useIsAdminUser()

  const fetchConfigs = () => {
    if(accountUUID && siteUUID) {
      loadCommentingConfig()
      if(hasTrendingNowV2 && !trendingNowId) loadTrendingNowV2Config()
    }
  }
  useEffect(() => {
    // 04.16.25 - https://insticatorinc.atlassian.net/browse/CMMT2-2583 - Deprecate 1.0 Product
    // if(sitesWithProductsV2.includes(siteUUID))fetchConfigs()
    fetchConfigs()
  }, [accountUUID, siteUUID, sitesWithProductsV2]) 

  const loadTrendingNowV2Config = useCallback(async()=>{
    const response = await dispatch(fetchTrendingNowV2Config({ siteUUID }))
    setIsLoading(false)
  }, [siteUUID, accountUUID])

  const loadCommentingConfig = useCallback(async()=>{
    const response = await dispatch(fetchCommentingConfigV2({ siteUUID }))
    setIsLoading(false)
  }, [siteUUID, accountUUID])
  

  const redirectToProductManager = () => dispatch(updateCurrentPage('/dashboard/productmanager'))

  const isConfigsLoading = ['initial', 'pending'].includes(commentingUnitConfig.status) 

  const isErrorLoadingConfig = commentingUnitConfig.status === 'rejected' || displayConfig.status === 'rejected'

  const commentingContent = useMemo(() => {
    const showLoadingScreen = isLoading || isConfigsLoading
    const showMigrationStatusScreen = migrationStatus === 'PROGRESS' || migrationStatus === 'DATA_MIGRATION_STARTED' || migrationStatus === 'ERROR'
    if(showLoadingScreen) return <LoadingState />
    
    if(showMigrationStatusScreen) return (
      <CardWrapper klass={'emptystate'}>
        <p>Currently unavailable during</p>
        <p>Commenting 2.0 migration process</p>
      </CardWrapper>
    )
    if(isErrorLoadingConfig) return (
      <EmptyState 
        graphicName={'error-500'} 
        bodycopy={
          <div>
            <div>There's an error fetching the Commenting Configurations. </div>
            <div>Please try again later.</div>
          </div>
        }             
        buttonText={'Try again'}
        buttonClick={fetchConfigs}
      />
    )
    return (
      <>
        { isAdmin &&
          <InlineMessage 
            key={'meow'}
            display={true}
            name={'productmanagermessage'}
            variant={'system'}
            icon={'exclamation-triangle'}
            message={
            <span>
              See <span className={'productmanagerlink'} onClick={redirectToProductManager}>Product Manager</span> to 
              enable/disable, get code, determine site placement, and more.
            </span>
            }
            close={false}
          />
        }
        <SiteThemeSection />
        <GeneralSection />
        <AdvancedSection />
        { isAdmin && 
        <>
          <AdsSection />
          <AdminSection />
        </>
        }
      </>
    )
  }, [migrationStatus, isLoading, isConfigsLoading])
  
  log('Disable test page', isLoading, isErrorLoadingConfig, isConfigsLoading)

  return (
    <Page name={'commentingconfigv2page'} klass={`${props.classes.commentingconfigv2page}`}>
      <SiteFilter disableTestPageButton={isLoading || isConfigsLoading || isErrorLoadingConfig} />
      <Content>
        <InContentMessage />
        <PageHeader title={'Commenting'} subtitle={'Configuration'}  />
        <PageContent>
          {commentingContent}
        </PageContent>
      </Content>
    </Page>
  )
})

/* ++++++++++ --------------- EXPORTS --------------- ++++++++++ */
export default CommentingConfigV2Page